<template>
    <div class="font-sans">
        <div class="mb-12">
            <h2 class="text-3xl text-slate-900 font-extrabold m-0">Contact Collector</h2>
            <p class="text-xl text-slate-700 font-normal my-4">
                Install a pop-up style chat window to collect name, phone and email from your users to add to Voxie.
                Just replace the company name and link to your terms in the example below.
            </p>
            <p class="text-base text-slate-500 font-normal m-0">
                Need help with installation? Our docs have examples &rarr;
            </p>
        </div>

        <div class="shadow rounded-2xl border border-white bg-slate-100 overflow-hidden">
            <div class="text-black text-lg font-extrabold bg-white border-b border-slate-200 py-4 px-6">
                <span>Interactive Preview</span>
            </div>
            <div class="slate-500 text-base font-normal flex flex-col lg:flex-row">
                <div class="lg:w-1/2 p-12">
                    <p class="mb-2">You can click on this preview to see how the collector will work on your site.</p>
                    <p>Theme customization is coming soon so you'll be able to tailor this to match your brand.</p>
                </div>
                <div class="lg:w-1/2 flex justify-center items-center py-10 bg-gray-300">
                    <pop-up-example></pop-up-example>
                </div>
            </div>
        </div>

        <div class="shadow rounded-2xl border border-white bg-slate-100 overflow-hidden mt-12">
            <div class="text-black text-lg font-extrabold bg-white border-b border-slate-200 py-4 px-6">
                <span>Installation</span>
            </div>
            <div class="slate-500 text-base font-normal p-12">
                <p class="slate-700 font-extrabold mb-4">1. Generate & Copy the code.</p>
                <pre
                    v-if="connectionKey"
                    class="bg-white border border-slate-200 rounded-2xl font-mono text-black font px-8 py-6"
                    ref="snippet"
                >
&lt;!-- Voxie Contact Collector --&gt;
&lt;script src="https://unpkg.com/@voxie/contacts.js@^1.8/dist/contacts.js"&gt;&lt;/script&gt;
&lt;script defer&gt;
    (async function () {
        window.voxie = await Voxie.init({
            publicKeyId: '{{ connectionKey.key_id }}',
            publicSecret: '{{ connectionKey.secret }}',
            connectBaseUri: '{{ connectionKey.connect_base_uri }}',
        });
        const popUp = window.voxie.popUp({
          // name: 'Company',
          // terms: 'https://example.com/terms',
        });
        popUp.auto();
    })();
&lt;/script&gt;</pre
                >
                <div class="my-4">
                    <vx-button
                        data-test="generate-copy-contact-collector"
                        @click="connectionKey ? copyScript() : generateConnectionKey()"
                        size="xl"
                        class="font-sans"
                        :disabled="loading"
                    >
                        {{ connectionKey ? 'Copy' : 'Generate' }}
                        <font-awesome-icon widthAuto :icon="connectionKey ? faCopy : faRefresh" />
                    </vx-button>
                </div>

                <hr class="border-slate-200" />
                <p class="slate-700 font-extrabold my-4">2. Paste in your site!</p>
                <p class="m-0">
                    This can be pasted right
                    <strong class="font-bold">before</strong> the closing &lt;/body&gt; tag of your website.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useToasted } from '../../../composables/useToasted';
import { connectionsClient } from '~/services';
import { ref, computed } from 'vue';
import PopUpExample from './contact-collector/PopUpExample.vue';
import { VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy, faRefresh } from '@fortawesome/pro-solid-svg-icons';

const connectionKey = ref(null);
const toasted = useToasted();
const loading = ref(false);
const snippet = ref(null);

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
    isTeamAdmin: {
        type: Boolean,
        default: false,
    },
});

const canGenerate = computed(() => props.ownsTeam || props.isTeamAdmin);

const copyScript = () => {
    navigator.clipboard.writeText(snippet.value.innerText);
};

const generateConnectionKey = async () => {
    if (!canGenerate.value) {
        toasted.error('Only the team owner or admin can generate the script.');
        return;
    }

    if (loading.value) {
        return false;
    }

    try {
        loading.value = true;
        const response = await connectionsClient.generateConnectionKey(Spark.state.currentTeam.id);

        connectionKey.value = response.data;
    } catch {
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};
</script>
